import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MetaTag from 'components/meta';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import InputText from 'components/common/InputText';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { isEmptyString, stringValidation } from 'utils/helper';
import { US_STATES, USStateType } from 'lib/usStateOptions';
import Label from 'components/common/label';
import { useSelector } from 'react-redux';
import { NullablePatientDTO } from 'types/patients';
import { RootState } from 'store/store';
import pick from 'lodash/pick';
import values from 'lodash/values';
import ZipCodeTextField from 'components/common/ZipCodeTextField';
import Wrapper from 'components/common/Wrapper';
import clsx from 'clsx';
import AddressSuggestion, { Place } from 'components/common/AddressSuggestion';
import { AddressDTO } from 'artifacts/dtos';
import { formatPlace } from 'utils/misc';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

type ContactFields = Partial<{
  place?: Place,
  unit?: string;
  street?: string;
  city?: string;
  contactState?: string;
  zip?: string;
  country?: string;
}>;

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

const checkFieldsKeyByIndex = ['street', 'city', 'contactState', 'zip'] as const;
const displayFields = ['street', 'unit', 'city', 'state', 'zipCode'] as const;

function _formatPlace(contactInfo: ContactFields) {
  return formatPlace({
    ...contactInfo,
    zipCode: contactInfo.zip ?? '',
    shortZipCode: contactInfo.zip ?? '',
    state: contactInfo.contactState ?? '',
  } as AddressDTO);
}

function ContactInformationScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const [contactInfo, setContactInfo] = useState<ContactFields>({
    place: null,
    unit: '',
    street: '',
    city: '',
    contactState: '',
    zip: '',
  });

  const patient = useSelector((state: RootState) => state.patient) as NullablePatientDTO;

  const [errors, _setErrors] = useState<Record<string, boolean>>({});
  const setErrors = (newErrors: Record<string, boolean>) => {
    _setErrors((prev) => ({ ...prev, ...newErrors }));
  };

  const [state, dispatch] = usePersistMHReducer();

  useEffect(() => {
    const contactInfoPage = state.find(
      (page: StateTypeLocalStorage) => page.name === actionPage,
    );
    const address = pick(patient?.address, displayFields);
    const contactValues = contactInfoPage?.value;

    const source: ContactFields = values(contactValues).some((val) => !isEmptyString(val))
      ? contactValues
      : {
        unit: address?.unit,
        street: address?.street,
        city: address?.city,
        contactState: address?.state,
        zip: address?.zipCode,
        ...(!isEmpty(address) ? formatPlace(address as AddressDTO) : {}),
      };

    setContactInfo(source);
  }, []);

  const selectedState = useMemo<USStateType | null>(() => US_STATES.find(
    (region) => (
      region.name === contactInfo?.contactState?.trim()
      || region.abbreviation === contactInfo?.contactState?.trim()
    ),
  ) ?? null, [contactInfo?.contactState]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: keyof ContactFields,
    validator?: (val: string) => boolean,
    maxLength: number = 60,
  ) => {
    const value = event.target.value.trimStart();
    if (value.length <= maxLength && (isNil(validator) || validator(value))) {
      setContactInfo((prev) => ({
        ...prev,
        ..._formatPlace({ ...contactInfo, [name]: value }),
        [name]: value,
      }));
      setErrors({ [name]: false });
    }
  };

  const onZipCodeChange = (value: string | number | null) => {
    setContactInfo((prev) => ({ ...prev, ..._formatPlace({ ...contactInfo, zip: value } as ContactFields), zip: value as string ?? '' }));
    setErrors({ zip: false });
  };

  const onContactStateChange = (e: unknown, value: USStateType | null) => {
    setContactInfo((prev) => ({
      ...prev,
      ..._formatPlace({ ...contactInfo, contactState: value?.abbreviation }),
      contactState: value?.abbreviation ?? '',
    }));
    setErrors({ contactState: isEmptyString(value) });
  };

  const clickNextOrSubmit = async (nextPage: number) => {
    const errorList: Record<string, boolean> = {};
    checkFieldsKeyByIndex.forEach((key) => {
      if (isEmptyString(contactInfo[key]) || (key === 'zip' && (contactInfo.zip?.length ?? 0) !== 5)) {
        errorList[key] = true;
      }
    });

    if (Object.keys(errorList).length > 0) {
      setErrors(errorList);
      return;
    }

    if (Object.values(errors).some(Boolean)) return;

    await dispatch({
      type: actionPage,
      name: 'Contact Information',
      value: contactInfo,
    });

    nextOrSubmitButton(nextPage);
  };

  const handleAddressChange = (address: AddressDTO) => {
    const place = formatPlace(address);
    setContactInfo({
      ...address,
      ...place,
      contactState: address?.state ?? '',
      zip: address?.shortZipCode ?? '',
    } as ContactFields);
    // clear errors
    _setErrors({});
  };

  return (
    <>
      <MetaTag title="Contact Information" />
      <Box pb={1}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Contact Information
          </h2>
          <Stack>
            <Label>Address</Label>
            <AddressSuggestion
              value={contactInfo.place}
              onChange={handleAddressChange}
            />
            <Label>Street</Label>
            <InputText
              onChange={(e) => { handleInputChange(e, 'street'); }}
              value={contactInfo.street}
              error={errors.street}
              name="street"
              id="street"
            />

            <p className={styles.separator} />

            <Label>Unit</Label>
            <InputText
              onChange={(e) => { handleInputChange(e, 'unit'); }}
              value={contactInfo.unit}
              name="unit"
              id="unit"
            />

            <p className={styles.separator} />

            <Label>City</Label>
            <InputText
              onChange={(e) => { handleInputChange(e, 'city', stringValidation); }}
              value={contactInfo.city}
              error={errors.city}
              name="city"
              id="city"
            />

            <p className={styles.separator} />

            <Label>State</Label>
            <Autocomplete<USStateType>
              options={US_STATES}
              value={selectedState}
              onChange={onContactStateChange}
              getOptionLabel={(option) => `${option?.name} - ${option?.abbreviation}`}
              renderInput={(params) => <TextField {...params} />}
              id="state"
              classes={{
                root: clsx('!w-full rounded', {
                  'has-error': errors.contactState,
                }),
              }}
            />

            <p className={styles.separator} />

            <Wrapper
              error={errors.zip}
              id="zip-code"
              label="Zip"
              showErrorText={false}
            >
              <ZipCodeTextField
                onChange={onZipCodeChange}
                value={contactInfo.zip}
              />
            </Wrapper>

            <p className={styles.separator} />
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => { clickNextOrSubmit(nextPage); }}>
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default ContactInformationScreen;
